import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box
} from '@mui/material';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import { useNavigate, Link } from 'react-router-dom';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import CommonRestricted from '../../common/CommonRestricted';

import { icons } from '../../common/CommonIcons';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useRates, rateTypes, targetTypes } from '../../../hooks/api/useRates';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

export default function ListShippingRates({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@rate"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useRates({ rateType: rateTypes.shippingRate, targetType: targetTypes.shippingRate });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.rate.id, scopeTypes.rate.shippingRate.id)

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = useRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            const result = await list(props)

            return {
                data: result?.data?.shipping_rates,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }
    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'rate_name',
                header: t('rateName'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`settings/shippingRate/update/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'type',
                header: t('type')
            },
            {
                name: 'location_name',
                header: t('location')
            },
            {
                name: 'status',
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, ["Enabled", "Disabled"], ["primary", "default"], "")
            }
        ])
    }

    const handleDelete = async () => {

        try {
            let result = await remove({ ids: selectedIds })

            promptMessage(true, result?.message)
            setRefreshDataGrid(currentVal => currentVal + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("settings/shippingRate/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        // false,
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ scopeType: scopeTypes[scopeIds.rate.id].shippingRule.id, functionType: functionTypes.pageList.id })) {
            props.push({
                displayText: t('manageRules'),
                onClick: () => {

                    navigate(getRoute(`settings/shippingRules`))
                }
            })
        }

        return props
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.rates })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("shippingRates")}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t("shippingRates"),
                        props: {
                            // underline: "hover",

                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}